/**
 * @file utils data
 */
import { cloneDeep, createEnumHelper, partition } from '@seiue/util'
import { PaperBlock, BlockTypeEnum } from 'packages/sdks-next/growp'

import { BaseNode, TreeNode } from './types'

export const blockTypeToLabel = createEnumHelper(() => ({
  [BlockTypeEnum.FirstLevelTitle]: '一级标题',
  [BlockTypeEnum.SecondLevelTitle]: '二级标题',
  [BlockTypeEnum.ThirdLevelTitle]: '三级标题',
  [BlockTypeEnum.FourthLevelTitle]: '四级标题',
  [BlockTypeEnum.Fulltext]: '资料',
  [BlockTypeEnum.Question]: '题目',
}))

/*
 * 默认的 comparator
 *
 * 按照 weight 从小到大排序，如果 weight 相同则 id 从大到小
 * 点击目录上方的➕号和新增子目录都是默认排放在上方
 *
 */
const DEFAULT_COMPARATOR = <T extends BaseNode>(a: T, b: T) =>
  (a.weight || 0) - (b.weight || 0) || b.id - a.id

/**
 * TODO: move to @seiue/util
 *
 * 将平铺的树节点的数组回转成树结构
 * {id: number; parentId: number; weight: number}[] => {id: number; children: {...}[], level: number; ...}
 *
 * @param arr - 平铺的树节点的数组
 * @param comparator - compareFn
 * @param level - 节点层级
 * @param parentId - 父节点 ID，如果为顶层节点则为 0
 * @returns Array<TreeNode<T>>
 */
export const arrayToTree = <T extends BaseNode>(
  arr: Array<T>,
  comparator = DEFAULT_COMPARATOR,
  level = 1,
  parentId = 0
): Array<TreeNode<T>> => {
  if (!arr.length) return []
  const [children, rest] = partition(arr, (a) => a.parentId === parentId)

  return children
    .map((c: T) => ({
      ...c,
      level,
      children: arrayToTree<T>(rest, comparator, level + 1, c.id).sort(
        comparator
      ),
    }))
    .sort(comparator)
}

export const HeaderTypes = [
  BlockTypeEnum.FirstLevelTitle,
  BlockTypeEnum.SecondLevelTitle,
  BlockTypeEnum.ThirdLevelTitle,
  BlockTypeEnum.FourthLevelTitle,
]

/**
 * 传入一个 blocks 数组，重新计算 weight 和 parentId，并返回需要更新的 blocks
 *
 * @param blks - 原数据
 * @returns 重新计算后的 blocks
 */
export const rearrangeBlocks = (blks: PaperBlock[]): PaperBlock[] => {
  const blocks = cloneDeep(blks)
  const headerTypes = HeaderTypes.slice().reverse()
  if (blocks.length) {
    const parentBlocks: PaperBlock[] = []

    /**
     * 对每一个块
     * 如果是标题，不断从 parentBlocks 中 pop，找到上一个层级大于它的标题
     *  设为它的父节点，并把他 push 进 parentBlocks 中
     * 如果不是标题，上级为 parentBlocks 中的最后一个标题
     */
    for (let i = 0; i < blocks.length; i += 1) {
      blocks[i].weight = i + 1

      if (headerTypes.includes(blocks[i].type)) {
        // 如果
        if (!parentBlocks.length) {
          blocks[i].parentId = 0
        } else {
          let parentId = 0

          while (parentBlocks.length) {
            const header = parentBlocks[parentBlocks.length - 1]

            const parentHeaderIndex = headerTypes.indexOf(header.type)
            const blockHeaderIndex = headerTypes.indexOf(blocks[i].type)

            if (parentHeaderIndex > blockHeaderIndex) {
              parentId = header.id
              break
            } else {
              parentBlocks.pop()
            }
          }

          blocks[i].parentId = parentId
        }

        parentBlocks.push(blocks[i])
      } else if (parentBlocks.length) {
        blocks[i].parentId = parentBlocks[parentBlocks.length - 1].id
      } else {
        blocks[i].parentId = 0
      }
    }
  }

  return blocks.filter((b) =>
    blks.find(
      (p) =>
        p.id === b.id && (p.weight !== b.weight || p.parentId !== b.parentId)
    )
  )
}

type GetChildrenBlocksOptions = {
  includeSelf: boolean
}

/**
 * 获取 id 下的所有子块
 *
 * @param id - id
 * @param blocks - 块数据
 * @param options - options
 * @returns 子数据
 */
export const getChildrenBlocks = (
  id: number,
  blocks: PaperBlock[],
  options: GetChildrenBlocksOptions = {
    includeSelf: true,
  }
) => {
  const block = blocks.find((b) => b.id === id)

  let ids = [id]
  let result: PaperBlock[] = []

  if (block) {
    if (options.includeSelf) {
      result = [block]
    }

    // 如果不是标题块，则无子块
    if (!HeaderTypes.includes(block.type)) return result

    while (ids.length) {
      const parentId = ids.shift()
      const toBeAdded = blocks.filter((b) => b.parentId === parentId)

      ids = ids.concat(
        toBeAdded.filter((b) => HeaderTypes.includes(b.type)).map((b) => b.id)
      )

      result = result.concat(toBeAdded)
    }
  }

  return result
}

/**
 * block 排序 comparator，按照 weight 从小到大，weight 相同时，id 从大到小
 *
 * @param a - 原数据
 * @param b - 带有新的排序信息的数据
 * @returns 排序后的数据
 */
export const blockComparator: (a: PaperBlock, b: PaperBlock) => number = (
  a: PaperBlock,
  b: PaperBlock
) => (a.weight || 0) - (b.weight || 0) || b.id - a.id
